import * as React from 'react';

import * as ramda from 'ramda';

import {colors} from 'shells/colors/colors';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {ProgressBar} from 'shells/progress-bar/progress-bar';
import {SettingsGroup} from 'shells/settings/settings-group';
import {SettingsHeader} from 'shells/settings/settings-header';
import {Body} from 'shells/typography/body';

import type {PeopleIqFilterSettingsUpdateInput as SelectedPeopleIqFilterSettings} from 'nutshell-graphql-types';

import {useGetPeopleIqFilterSettings} from '../../people-iq/graphql/hooks/use-get-people-iq-filter-settings';
import {useGetUnlockInfo} from '../../people-iq/graphql/hooks/use-get-unlock-info';
import {useUpdatePeopleIqFilterSettings} from '../../people-iq/graphql/hooks/use-update-people-iq-filter-settings';
import {
    getAllSettingsFromFilterSettings,
    getHeaderAndSubheaderBySettingsGroupKey,
    getSelectedFilterSettings,
    handleSettingSelectionChange,
} from '../../people-iq/settings/helpers';
import type {SettingsGroupKey} from '../../people-iq/settings/helpers';

export function PeopleIqSettings() {
    const {unlockInfo} = useGetUnlockInfo();
    const {peopleIqFilterSettings} = useGetPeopleIqFilterSettings();
    const {
        updatePeopleIqFilterSettings,
        isLoading: isSaving,
        error,
    } = useUpdatePeopleIqFilterSettings();

    const [selectedSettings, setSelectedSettings] = React.useState<
        SelectedPeopleIqFilterSettings | null | undefined
    >(undefined);

    const allSettings =
        peopleIqFilterSettings && getAllSettingsFromFilterSettings(peopleIqFilterSettings, false);

    React.useEffect(() => {
        if (peopleIqFilterSettings) {
            // @ts-expect-error
            setSelectedSettings(getSelectedFilterSettings(peopleIqFilterSettings, false));
        }
    }, [peopleIqFilterSettings]);

    return (
        <div>
            {unlockInfo ? (
                <div className='mb-16'>
                    <ProgressBar
                        percentComplete={
                            (unlockInfo.unlockLimit - unlockInfo.unlocksRemaining) /
                            unlockInfo.unlockLimit
                        }
                        height={4}
                        roundedCorners={true}
                        showPercentage={true}
                        backgroundColor={colors.offWhiteDk}
                        color={colors.grey}
                    />
                    <Body color='light' size='small'>
                        {unlockInfo.unlockedThisMonth.toLocaleString()} credits have been used this
                        month. You can use {unlockInfo.unlocksRemaining.toLocaleString()} more
                        credits out of the available {unlockInfo.unlockLimit.toLocaleString()} in
                        your plan this month.
                    </Body>
                </div>
            ) : undefined}
            <SettingsHeader
                title='Filter'
                subtitle='Use these filters to refine your PeopleIQ recommendations across all companies.'
                onSave={() => {
                    if (selectedSettings) {
                        updatePeopleIqFilterSettings(selectedSettings);
                    }
                }}
                isSaving={isSaving}
                error={error}
                disabled={
                    ramda.equals(
                        selectedSettings,
                        // @ts-expect-error
                        peopleIqFilterSettings && getSelectedFilterSettings(peopleIqFilterSettings)
                    ) || isSaving
                }
            />
            {allSettings && selectedSettings ? (
                <StackLegacy spacing={12}>
                    {/* @ts-expect-error */}
                    {Object.keys(allSettings).map((settingsGroupKey: SettingsGroupKey) => (
                        <SettingsGroup
                            key={settingsGroupKey}
                            {...getHeaderAndSubheaderBySettingsGroupKey(settingsGroupKey)}
                            // @ts-expect-error
                            allGroupSettings={allSettings[settingsGroupKey]}
                            // @ts-expect-error
                            selectedGroupSettings={selectedSettings[settingsGroupKey]}
                            onSettingChange={(settingId, checked) =>
                                handleSettingSelectionChange(
                                    setSelectedSettings,
                                    settingsGroupKey,
                                    settingId,
                                    checked
                                )
                            }
                            disabled={isSaving}
                        />
                    ))}
                </StackLegacy>
            ) : (
                <LoadingPage padding={0} />
            )}
        </div>
    );
}
